import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Checkbox,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  clearBreakPlanResult,
  depositPlanBreakRequest,
} from "../../redux/DepositePlan/depositeSlice";
import { useAppTheme } from "../../theme/useAppTheme";

export default function DepositDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, breakPlanResult, error } = useSelector(
    (state) => state.deposit,
  );
  useEffect(() => {
    if (breakPlanResult?.success) {
      navigate("/depositPlanHistory");
      dispatch(clearBreakPlanResult());
    }
  }, [breakPlanResult, navigate, dispatch]);

  const themeColors = useAppTheme();

  const { plan } = location.state || {};
  const [agreeTerms, setAgreeTerms] = useState(false);

  if (!plan) {
    return (
      <Box textAlign="center" mt={10}>
        <Typography>No data found</Typography>
      </Box>
    );
  }

  const depositPlan = plan.depositplan;

  const benefit = (
    (((depositPlan.qty * depositPlan.interest) / 100) / 365) *
    depositPlan.duration
  ).toFixed(4);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          maxWidth: 1000,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
          background: themeColors.scaffoldGradientColors,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Box sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>
          {/* Page Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            align="center"
            sx={{
              mb: 3,
              fontSize: { xs: "1.8rem", sm: "2.2rem" },
              background: themeColors.primary,
              backgroundClip: "text",
              textFillColor: "transparent",
            }}
          >
            Discontinue Plan
          </Typography>

          {/* Plan Name */}
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", mb: 3, textAlign: "center" }}
          >
            {depositPlan.name}
          </Typography>

          {/* Metal Gradient */}
          <Box
            sx={{
              background:
                plan.bullion.metalName === "Gold"
                  ? "linear-gradient(135deg, #FFD700, #DAA520)"
                  : plan.bullion.metalName === "Silver"
                    ? "linear-gradient(135deg, #C0C0C0, #A9A9A9)"
                    : "linear-gradient(135deg, #E5E4E2, #D3D3D3)",
              borderRadius: "20px",
              p: 2,
              mb: 3,
              textAlign: "center",
              color: "#ffffff",
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {plan.bullion.metalName}-{plan.bullion.karat}k-
              {plan.bullion.purity}
            </Typography>
          </Box>

          {/* Duration & Min Qty */}
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="body2">Duration</Typography>
              <Typography variant="h6">
                {depositPlan.duration}{" "}
                <Typography component="span" variant="body2">
                  {depositPlan.unit.toLowerCase()}
                </Typography>
              </Typography>
            </Box>

            <Box textAlign="right">
              <Typography variant="body2">Min Qty</Typography>
              <Typography variant="h6">
                {depositPlan.qty}{" "}
                <Typography component="span" variant="body2">
                  gm(s)
                </Typography>
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ background: themeColors.text, my: 2 }} />

          {/* Benefit */}
          <Typography variant="body2" align="center" sx={{ mb: 3 }}>
            Upon completion of this plan's duration, you will receive a benefit
            of{" "}
            <Typography component="span" fontWeight={800}>
              {benefit} gm(s)
            </Typography>{" "}
            for every {depositPlan.qty} gm(s) deposited.
          </Typography>
          {/* Deposit Quantity */}
          <Card
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              textAlign: "center",
              background: themeColors.mediumContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography fontWeight="bold">Gold Deposit Quantity</Typography>

            <Typography
              variant="caption"
              sx={{ fontSize: "1rem", fontWeight: 740 }}
            >
              {plan.bullion.karat}K - {plan.bullion.purity}
            </Typography>

            <Box
              sx={{
                mt: 2,
                color: themeColors.text,
                border: `1px dashed ${themeColors.scaffold}`,
                borderRadius: 2,
                p: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h6">{plan.weight} gm(s)</Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                mb: 1,
                mt: 2,
              }}
            >
              <GppGoodIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
              <Typography variant="caption">
                100% Safe & Secured | Pure {plan.bullion.metalName}
              </Typography>
            </Box>
          </Card>

          {/* Auto Renew */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: themeColors.mediumContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "15px",
              p: 2,
              mb: 3,
            }}
          >
            <Checkbox
              checked={plan.autorenew}
              disabled
              sx={{ color: "#ffffff" }}
            />
            <Box>
              <Typography fontWeight={500}>Auto Renew</Typography>
            </Box>
          </Box>

          <Card
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 3,
              background: themeColors.mediumContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography fontWeight="bold">Terms of cancellation</Typography>
            <Typography variant="caption">• {plan?.comment}</Typography>

            <Stack direction="row" alignItems="center" mt={1}>
              <Checkbox
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                sx={{ color: "#ffffff" }}
              />
              <Typography>I agree</Typography>
            </Stack>
          </Card>

          {/* Confirm Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={() =>
              dispatch(
                depositPlanBreakRequest({
                  id: plan._id,
                  endDate: String(Date.now()),
                }),
              )
            }
            disabled={!agreeTerms || loading}
            sx={{
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
              fontWeight: 500,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              px: 5,
              py: 1.2,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            {loading ? "Processing..." : "Confirm"}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
